/**
 * Quote form: collect the fields, post the cart to the app proxy, show the
 * reference.
 *
 * The cart lines are read from a JSON script tag rendered by Liquid, never
 * scraped from the DOM, so no price or title can reach the request. The service
 * refuses to read money from a request in any case, but the two halves agreeing
 * is what makes that rule easy to keep.
 *
 * Failure is treated as the normal case, because it is the one a customer
 * remembers: every error code from the service maps to a sentence saying what
 * to do next, and the submit button always comes back.
 */
(() => {
  const form = document.querySelector('[data-quote-form]');
  const confirmation = document.querySelector('[data-quote-confirmation]');
  const main = document.querySelector('[data-quote-main]');
  const strings = window.quoteStrings || {};

  /** Show the confirmation from ?done=<ref>, so a reload still makes sense. */
  const showDone = () => {
    if (!confirmation) return false;

    const reference = new URLSearchParams(window.location.search).get('done');
    if (!reference) return false;

    // The reference is echoed into the page, so accept only what a reference
    // can be. Anything else is shown as a request that succeeded without one
    // rather than being written to the DOM.
    const safe = /^[A-Za-z0-9-]{1,32}$/.test(reference) ? reference : '';

    const slot = confirmation.querySelector('[data-quote-reference]');
    if (slot) slot.textContent = safe;
    confirmation.hidden = false;
    if (main) main.hidden = true;

    // By class: the heading is an h2 since 2026-10-03 (the page's only h1 is the form's title).
    const heading = confirmation.querySelector('.dc-quote-done-title');
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    }
    return true;
  };

  if (showDone()) return; // nothing else to wire up on the confirmation page
  if (!form) return;

  const status = form.querySelector('[data-quote-status]');
  const submit = form.querySelector('[data-quote-submit]');
  const submitLabel = form.querySelector('[data-quote-submit-label]');
  const originalLabel = submitLabel ? submitLabel.textContent : '';

  const payloadTag = document.querySelector('[data-quote-payload]');
  let payload = null;
  try {
    payload = JSON.parse(payloadTag.textContent);
  } catch (error) {
    // Without the cart there is nothing to send; leave the form inert rather
    // than posting something incomplete.
    console.error('quote form: could not read the cart payload', error);
    return;
  }

  const setStatus = (message, isError = true, { scroll = true } = {}) => {
    if (!status) return;
    status.hidden = false;
    status.textContent = message;
    status.classList.toggle('quote-form-status-error', isError);
    if (scroll) status.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const clearStatus = () => {
    if (status) status.hidden = true;
  };

  /**
   * Mark one field as the problem: red outline (aria-invalid), a helper line under it saying what
   * it needs, focus, and scroll to it rather than to the status line, which used to win the
   * scroll and leave nothing visibly wrong (Sam, 2026-10-01). Both clear as soon as the field is
   * edited.
   */
  const hints = strings.fieldHints || {};

  const unmark = (field) => {
    field.removeAttribute('aria-invalid');
    field.removeAttribute('aria-describedby');
    const hint = field.parentNode.querySelector('.dc-field-hint');
    if (hint) hint.remove();
  };

  const clearInvalid = () => {
    form.querySelectorAll('[aria-invalid="true"]').forEach(unmark);
  };

  const markInvalid = (field) => {
    field.setAttribute('aria-invalid', 'true');

    const text = hints[field.name] || hints.other;
    if (text && field.id) {
      const hint = document.createElement('span');
      hint.className = 'dc-field-hint';
      hint.id = `${field.id}Hint`;
      hint.textContent = text;
      field.after(hint);
      field.setAttribute('aria-describedby', hint.id);
    }

    field.addEventListener('input', () => unmark(field), { once: true });
    field.addEventListener('change', () => unmark(field), { once: true });
    setStatus(strings.errorValidation || strings.errorGeneric, true, { scroll: false });
    field.focus({ preventScroll: true });
    field.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  // The service names the field it refused (`field` in its 400 reply). These are its names for
  // the form's own fields; anything else (origin, lines, locale) is not the visitor's to fix.
  const SERVICE_FIELDS = {
    company: 'company',
    vatId: 'vatId',
    poReference: 'poReference',
    contactName: 'contactName',
    email: 'email',
    phone: 'phone',
    'address.street': 'street',
    'address.postcode': 'postcode',
    'address.city': 'city',
    'address.country': 'country',
    requiredBy: 'requiredBy',
    requirements: 'requirements',
  };

  const setBusy = (busy) => {
    if (submit) submit.disabled = busy;
    if (submitLabel) submitLabel.textContent = busy ? (strings.submitting || '…') : originalLabel;
  };

  const value = (name) => {
    const field = form.elements[name];
    return field ? field.value.trim() : '';
  };

  /**
   * Native validity, reported one field at a time. `novalidate` is on the form
   * so the browser does not scroll to a field before the status is written.
   * Every field, not only required ones: the pattern on phone and VAT and the
   * minimum date mirror what the service refuses.
   */
  const firstInvalid = () => {
    const fields = Array.from(form.querySelectorAll('input, select, textarea'));
    return fields.find((field) => field.name !== 'hp' && !field.checkValidity()) || null;
  };

  const messageFor = (response, body) => {
    const code = body && body.error;
    if (code === 'invalid') return strings.errorValidation || strings.errorGeneric;
    if (code === 'rate_limited') return strings.errorRateLimited || strings.errorGeneric;
    if (code === 'duplicate_recent') return strings.errorDuplicate || strings.errorGeneric;
    if (code === 'unknown_variant') return strings.errorUnavailable || strings.errorGeneric;
    return strings.errorGeneric;
  };

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    clearStatus();
    clearInvalid();

    const invalid = firstInvalid();
    if (invalid) {
      markInvalid(invalid);
      return;
    }

    const body = {
      ...payload,
      company: value('company'),
      vatId: value('vatId'),
      poReference: value('poReference'),
      contactName: value('contactName'),
      email: value('email'),
      phone: value('phone'),
      address: {
        street: value('street'),
        postcode: value('postcode'),
        city: value('city'),
        country: value('country'),
      },
      requiredBy: value('requiredBy'),
      requirements: value('requirements'),
      hp: value('hp'),
    };

    setBusy(true);

    let response;
    let json = null;
    try {
      response = await fetch(window.quoteEndpoint || '/apps/quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      json = await response.json().catch(() => null);
    } catch (error) {
      setBusy(false);
      setStatus(strings.errorNetwork || strings.errorGeneric);
      return;
    }

    // A duplicate is not a failure: the first request created a draft, and the
    // service hands back its reference, so show the customer that one.
    const duplicate = response.status === 409 && json && json.ref;

    if (!response.ok && !duplicate) {
      setBusy(false);
      const fieldName = json && json.error === 'invalid' ? SERVICE_FIELDS[json.field] : null;
      const field = fieldName ? form.elements[fieldName] : null;
      if (field) {
        markInvalid(field);
      } else {
        setStatus(messageFor(response, json));
      }
      return;
    }

    const reference = json && json.ref ? json.ref : '';

    // The cart is kept (D10) but nothing is written to it: the cart page's quote
    // card stays as it was, so another quote is one click away (Sam, 2026-10-01).

    // Land on ?done=<ref> so a reload or a shared link still shows the
    // confirmation rather than an empty form.
    const url = new URL(window.location.href);
    url.searchParams.set('done', reference);
    window.location.replace(url.toString());
  });
})();
